import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import type { LibraryDto } from "@/lib/lutely-api/gen/dtos";
import {
  getLibraryControllerGetLibraryQueryKey,
  useLibraryControllerToggleFollowCreator,
  useLibraryControllerToggleSaveAlbum,
  useLibraryControllerToggleSaveTrack,
} from "@/lib/lutely-api/gen/library/library";

type LibraryIdField = keyof LibraryDto;

type OptimisticContext = { previous?: LibraryDto };

const toggleId = (ids: string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id];

/**
 * Optimistic toggle handlers over the `GET /library` cache: flip the id in the
 * given field immediately, roll back to the snapshot on error, and re-sync with
 * the server when the mutation settles.
 */
const useOptimisticLibraryToggle = (field: LibraryIdField) => {
  const queryClient = useQueryClient();
  const queryKey = getLibraryControllerGetLibraryQueryKey();

  return {
    onMutate: async (id: string): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<LibraryDto>(queryKey);
      queryClient.setQueryData<LibraryDto>(queryKey, (old) =>
        old ? { ...old, [field]: toggleId(old[field], id) } : old,
      );
      return { previous };
    },
    onError: (context: OptimisticContext | undefined) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error("Couldn't update your library. Please try again.");
    },
    // Re-sync the id lists AND the /library/tracks + /library/creators pages
    onSettled: () =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/library"),
      }),
  };
};

export const useToggleSaveTrack = () => {
  const toggle = useOptimisticLibraryToggle("savedTrackIds");
  return useLibraryControllerToggleSaveTrack({
    mutation: {
      onMutate: ({ trackId }) => toggle.onMutate(trackId),
      onError: (_error, _variables, context) => toggle.onError(context),
      onSettled: toggle.onSettled,
    },
  });
};

export const useToggleSaveAlbum = () => {
  const toggle = useOptimisticLibraryToggle("savedAlbumIds");
  return useLibraryControllerToggleSaveAlbum({
    mutation: {
      onMutate: ({ albumId }) => toggle.onMutate(albumId),
      onError: (_error, _variables, context) => toggle.onError(context),
      onSettled: toggle.onSettled,
    },
  });
};

export const useToggleFollowCreator = () => {
  const toggle = useOptimisticLibraryToggle("followedCreatorIds");
  return useLibraryControllerToggleFollowCreator({
    mutation: {
      onMutate: ({ creatorId }) => toggle.onMutate(creatorId),
      onError: (_error, _variables, context) => toggle.onError(context),
      onSettled: toggle.onSettled,
    },
  });
};
